import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Instagram, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();
  const partners = ['We Advise', 'CIBA Congress', 'COSMOS 3D', 'MODE Modular'];
  
  return (
    <footer className="bg-neutral-950 text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-4 gap-12">
          <div className="lg:col-span-1">
            <div className="text-3xl font-bold mb-4">
              <span className="font-light">We</span>
              <span className="font-black">Develop</span>
            </div>
            <p className="text-neutral-400 text-sm leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              <a href="https://linkedin.com/company/wedevelop" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center hover:bg-red-600 transition-colors">
                <Linkedin size={18} />
              </a>
              <a href="https://instagram.com/wedevelop" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center hover:bg-red-600 transition-colors">
                <Instagram size={18} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-6">{t('footer.navigation')}</h4>
            <ul className="space-y-3">
              {[
                { path: '/', label: t('nav.home') },
                { path: '/3d-concrete-printing', label: t('nav.printing3d') },
                { path: '/modular-platform', label: t('nav.modular') },
                { path: '/about', label: t('nav.about') },
                { path: '/contact', label: t('nav.contact') }
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path}
                    className="text-neutral-400 hover:text-white transition-colors flex items-center gap-2 group">
                    {link.label}
                    <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-6">{t('footer.services')}</h4>
            <ul className="space-y-3 text-neutral-400">
              <li>{t('footer.service1')}</li>
              <li>{t('footer.service2')}</li>
              <li>{t('footer.service3')}</li>
              <li>{t('footer.service4')}</li>
              <li>{t('footer.service5')}</li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-6">{t('footer.contact')}</h4>
            <ul className="space-y-4">
              <li>
                <a href="mailto:modular@wedevelop.one" className="flex items-center gap-3 text-neutral-400 hover:text-white transition-colors">
                  <Mail size={18} className="text-red-500" />
                  modular@wedevelop.one
                </a>
              </li>
              <li>
                <a href="https://wa.me/5491112345678" className="flex items-center gap-3 text-neutral-400 hover:text-white transition-colors">
                  <Phone size={18} className="text-red-500" />
                  WhatsApp
                </a>
              </li>
              <li className="flex items-start gap-3 text-neutral-400">
                <MapPin size={18} className="text-red-500 mt-1" />
                <span>Buenos Aires, Argentina<br />Montevideo, Uruguay</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-neutral-800">
          <p className="text-sm text-neutral-500 mb-4">{t('footer.supportedBy')}</p>
          <div className="flex flex-wrap gap-6">
            {partners.map((partner, index) => (
              <span key={index} className="text-neutral-400 text-sm font-medium">{partner}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-neutral-500 text-sm">
            © {new Date().getFullYear()} We Develop. {t('footer.rights')}
          </p>
          <p className="text-neutral-600 text-xs max-w-xl text-center md:text-right">
            {t('footer.disclaimer')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
